import React, { useState, useEffect } from "react";

import get from "lodash/get";
import initial from "lodash/initial";
import flatMap from "lodash/flatMap";

import MenuOption from "./MenuOption";
import { CustomContextMenu } from "./customContextMenu/CustomContextMenu";
import { TStylableComponent } from "../../types/ui/TStylableComponent";

export type TMenuOption = {
  label: string;
  disabled?: boolean;
  children?: TMenuOption[];
  callBack?: () => void;
};

interface IContextMenuOptionsPropTypes {
  options: TMenuOption[];
  openFromElement: HTMLElement | null;
  hideParent: () => void;
  parentRef?: HTMLElement | null;
  shouldCloseOnSelect?: boolean;
}

const CHILDREN_KEY = "children";

const ContextMenuOptions: React.FC<
  TStylableComponent<IContextMenuOptionsPropTypes>
> = ({
  options,
  openFromElement,
  hideParent,
  parentRef,
  shouldCloseOnSelect = false,
}: IContextMenuOptionsPropTypes) => {
  const [levelOptions, setLevelOptions] = useState<TMenuOption[]>(options);
  const [pathKeys, setPathKeys] = useState<number[]>([]);

  const isFirstLevelOptions = pathKeys.length === 0;

  const updateLevelOptions = (path: number[]): void => {
    const pathWithChildren = flatMap(path, (element) => [
      element,
      CHILDREN_KEY,
    ]);
    if (pathWithChildren.length) {
      const option = get(options, initial(pathWithChildren));
      setLevelOptions([option, ...option.children]);
    } else {
      setLevelOptions(options);
    }
  };

  useEffect(() => {
    if (options) {
      updateLevelOptions(pathKeys);
    }
  }, [options, pathKeys]);

  const handleOptionClick = (option: TMenuOption, index: number): void => {
    if (!isFirstLevelOptions && index === 0 && option.children?.length) {
      handleBeforeOptionClick();
    } else if (option.children?.length) {
      handleChildOptionClick(index);
    } else {
      option.callBack && option.callBack();
      shouldCloseOnSelect && closeContextMenu();
    }
  };

  const handleBeforeOptionClick = (): void => {
    const previousPathKeys = initial(pathKeys);
    setPathKeys(previousPathKeys);
  };

  const handleChildOptionClick = (index: number): void => {
    const optionIndex = isFirstLevelOptions ? index : index - 1;
    setPathKeys((prev) => [...prev, optionIndex]);
  };

  const closeContextMenu = (): void => {
    hideParent();
    setLevelOptions(options);
    setPathKeys([]);
  };

  const handleClose = (e?: React.SyntheticEvent): void => {
    if (e) {
      e.stopPropagation?.();
    }
    closeContextMenu();
  };

  return (
    <CustomContextMenu
      parentRef={parentRef}
      anchorEl={openFromElement}
      onClose={handleClose}
    >
      {levelOptions.map((option, index) => (
        <MenuOption
          key={`${option.label}_${index}`}
          option={option}
          index={index}
          isLast={index === levelOptions.length - 1}
          isFirstLevelOptions={isFirstLevelOptions}
          onOptionClick={handleOptionClick}
        />
      ))}
    </CustomContextMenu>
  );
};

export default ContextMenuOptions;
